/**
 * Multimedia Grid
 */
import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, getViewportSize } from 'js/helpers.js';
const componentName = 'multimedia-grid';

class MultimediaGrid extends Component {

    constructor(element) {
        super(element);
    }

    /**
     * Component Setup
     */

    // Elements
    elements = {
        container: this.element.querySelector(`.${componentName}__container`),
        thumbnailList: this.element.querySelector(`.${componentName}__thumbnail-list`),
        thumbnails: this.element.getElementsByClassName(`${componentName}__thumbnail`),
        activeMediaParent: this.element.querySelector(`.${componentName}__active-media`),
        prevButton: this.element.querySelector(`.${componentName}__previous`),
        nextButton: this.element.querySelector(`.${componentName}__next`),
        mobileSlider: this.element.querySelector(`.${componentName}__mobile-slider`)
    };

    // Util & State
    currentViewport = getViewportSize();

    sliderPositionX = 0;
    totalSlides = this.elements.thumbnails.length;

    getThumbnailHeight = () => {
        return (this.elements.thumbnails?.[0]?.offsetHeight ?? 84) + 8;
    }

    /**
     * Populate Active Media
     * @param { DOM Element } thumbnailEle - thumbnail element to show the selected active media
     */
    populateActiveMedia = (thumbnailEle) => {
        const slideId = thumbnailEle.getAttribute('data-slide');

        // Hide all active media elements
        const activeMediaElements = this.elements.container.querySelectorAll(`.${componentName}__active-media`);
        activeMediaElements.forEach((element) => element.classList.add('hidden'));

        // Show the selected media element
        const targetElement = this.element.querySelector(`[id="${slideId}"]`);
        if (targetElement) {
            targetElement.classList.remove('hidden');
        }

        // Recalibrate video size
        if (typeof (Event) === 'function') {
            // modern browsers
            window.dispatchEvent(new Event('resize'));
        } else {
            // for IE and other old browsers
            // causes deprecation warning on modern browsers
            const evt = window.document.createEvent('UIEvents');
            evt.initUIEvent('resize', true, false, window, 0);
            window.dispatchEvent(evt);
        }
    };

    /**
     * Update Desktop Carousel
     * @param { String } direction - pass in 'previous' or 'next' to transition slides
     */
    updateDesktopCarousel = (direction) => {
        const desktopSlides = 4;
        if(this.totalSlides > desktopSlides) {
            const maxTransition = -((this.totalSlides - desktopSlides) * this.getThumbnailHeight());
            const prevTransition = this.sliderPositionX + this.getThumbnailHeight();
            const nextTransition = this.sliderPositionX - this.getThumbnailHeight();

            // Update Direction if passed in
            if(direction) {
                this.sliderPositionX = direction === 'next' ? nextTransition : prevTransition;
                this.elements.thumbnailList.style.transform = `translateY(${this.sliderPositionX}px)`;
            }

            // Update Show/Hide of Buttons
            this.elements.prevButton.classList.remove('active');
            this.elements.nextButton.classList.remove('active');

            if(this.sliderPositionX === 0) this.elements.nextButton.classList.add('active');
            if(this.sliderPositionX < 0) {
                this.elements.prevButton.classList.add('active');
                this.elements.nextButton.classList.add('active');
            }
            if(this.sliderPositionX === maxTransition) this.elements.nextButton.classList.remove('active');
        }
    };

    /**
     * On Thumbnail Click (Desktop)
     */
    onThumbnailClick = (e) => {
        const thumbnailEle = e.currentTarget;
        if(!thumbnailEle.classList.contains('active') && e.isTrusted) {
            [...this.elements.thumbnails].forEach((ele) => ele.classList.remove('active'));
            this.populateActiveMedia(thumbnailEle);
            thumbnailEle.classList.add('active');
        }
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        // Component Listeners
        [...this.elements.thumbnails].forEach((ele) => ele.addEventListener('click', (e) => {
            if(this.currentViewport === 'desktop') this.onThumbnailClick(e);
        }));
        this.elements.prevButton.addEventListener('click', () => this.updateDesktopCarousel('previous'));
        this.elements.nextButton.addEventListener('click', () => this.updateDesktopCarousel('next'));
        if (this.totalSlides > 5) {
            this.elements.nextButton.classList.add('active');
        }


        const imageThumbnails = document.querySelectorAll(`.${componentName}__header-image-gallery-frame`);
        const galleryModal = document.querySelector(`.${componentName}__gallery-modal`);
        const desktopMainImages = document.querySelectorAll(`.multimedia-grid .main-image`);
        Array.from(desktopMainImages).forEach((image) => {
            image.addEventListener('click', (event) => {
                if (image.classList.contains(`${componentName}__default-image`)) {
                    return;
                }
                const modalImage = galleryModal.querySelector(`.${componentName}__gallery-modal-image`);
                modalImage.src = image.src;
                const modalListImages = galleryModal.querySelectorAll(`.${componentName}__gallery-modal-list-frame`);
                modalListImages.forEach((frame) => {
                  const listImg = frame.querySelector(`.${componentName}__gallery-modal-list-image`);
                  if (!listImg) return;

                  // Compare src (normalize via .src which becomes absolute URL)
                  const isMatch = listImg.src === image.src;

                  if (isMatch) {
                    frame.classList.add('active');
                  } else {
                    frame.classList.remove('active');
                  }
                });

                galleryModal.classList.add('active');
            });
            image.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    image.click();
                }
            });
        });
        const galleryModalListFrames = galleryModal.querySelectorAll(`.${componentName}__gallery-modal-list-frame`);
        const mobileMainImages = document.querySelectorAll(`.${componentName}__main-image`);

        Array.from(imageThumbnails).forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', (event) => {
                const image = thumbnail.querySelector('img');
                Array.from(mobileMainImages).forEach((mainImage) => {
                    mainImage.src = image.src;
                });
                [...galleryModalListFrames].find((frame) => frame.querySelector('img').src === image.src)?.click();
            });

            thumbnail.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    thumbnail.click();
                }
            });
        });
        const galleryModalBackground = galleryModal.querySelector(`.${componentName}__gallery-modal-background`);
        galleryModalBackground?.addEventListener('click', (event) => {
            galleryModal.classList.remove('active');
        });
        const galleryModalImage = galleryModal.querySelector(`.${componentName}__gallery-modal-image`);
        if(!galleryModal.dataset.arrowsBound){
            galleryModal.dataset.arrowsBound = 'true';
            const galleryModalMainArrows = galleryModal.querySelectorAll(`.${componentName}__gallery-modal-main-arrow`);
            Array.from(galleryModalMainArrows).forEach((arrow) => {
                arrow.addEventListener('click' , (event) => {
                    const currentImage = galleryModal.querySelector(`.${componentName}__gallery-modal-list-frame.active`);
                    const arrowDirection = arrow.getAttribute('data-direction');
                    let nextImage;
                    if (arrowDirection === 'next') {
                        nextImage = currentImage.nextElementSibling ? currentImage.nextElementSibling : currentImage.parentElement.firstElementChild;
                    } else if (arrowDirection === 'prev') {
                        nextImage = currentImage.previousElementSibling ? currentImage.previousElementSibling : currentImage.parentElement.lastElementChild;
                    }
                    currentImage.classList.remove('active');
                    nextImage.classList.add('active');
                    galleryModalImage.src = nextImage.querySelector('img').src;
                });
                arrow.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter') {
                        arrow.click();
                    }
                });
            });
                }
        Array.from(galleryModalListFrames).forEach((frame) => {
            frame.addEventListener('click', (event) => {
                if (frame.classList.contains('active')) {
                    return;
                }
                const currentImage = galleryModal.querySelector(`.${componentName}__gallery-modal-list-frame.active`);
                currentImage.classList.remove('active');
                frame.classList.add('active');
                galleryModalImage.src = frame.querySelector('img').src;
            });
            frame.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    frame.click();
                }
            });
        });
        if (galleryModalImage) {
            galleryModalImage.addEventListener('click', (event) => {
                if (galleryModalImage.classList.contains(`${componentName}__gallery-modal-image--zoomed-out`)) {
                    galleryModalImage.style.setProperty('--horizontal-origin', `${(event.offsetX / galleryModalImage.clientWidth) * 100}%`);
                    galleryModalImage.style.setProperty('--vertical-origin', `${(event.offsetY / galleryModalImage.clientHeight) * 100}%`);
                    galleryModalImage.classList.remove(`${componentName}__gallery-modal-image--zoomed-out`);
                    galleryModalImage.classList.add(`${componentName}__gallery-modal-image--zoomed-in`);
                } else {
                    galleryModalImage.classList.remove(`${componentName}__gallery-modal-image--zoomed-in`);
                    galleryModalImage.classList.add(`${componentName}__gallery-modal-image--zoomed-out`);
                }
            });
            let mouseEventDebounce = false;
            const mouseEventDebounceTimer = 5;
            galleryModalImage.addEventListener('mousemove', (event) => {
                if (mouseEventDebounce) {
                    return;
                }
                galleryModalImage.style.setProperty('--horizontal-origin', `${(event.offsetX / galleryModalImage.clientWidth) * 100}%`);
                galleryModalImage.style.setProperty('--vertical-origin', `${(event.offsetY / galleryModalImage.clientHeight) * 100}%`);
                mouseEventDebounce = true;
                setTimeout(() => {
                    mouseEventDebounce = false;
                }, mouseEventDebounceTimer);
            });
        }
        const galleryModalListArrows = galleryModal.querySelectorAll(`.${componentName}__gallery-modal-list-arrow`);
        const galleryModalList = galleryModal.querySelector(`.${componentName}__gallery-modal-list-container`);
        Array.from(galleryModalListArrows).forEach((arrow) => {
            arrow.addEventListener('click', () => {
                const arrowDirection = arrow.getAttribute('data-direction');
                this.sideScroll(galleryModalList, arrowDirection, 10, 200, 10);
            });
            arrow.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    arrow.click();
                }
            });
        });

        const galleryHeaderListArrows = document.querySelectorAll(`.${componentName}__header-image-gallery-arrow`);
        const galleryHeaderList = document.querySelector(`.${componentName}__header-image-gallery-container`);
        Array.from(galleryHeaderListArrows).forEach((arrow) => {
            arrow.addEventListener('click', () => {
                const arrowDirection = arrow.getAttribute('data-direction');
                this.verticalScroll(galleryHeaderList, arrowDirection, 10, 200, 10);

                // If the gallery is scrolled to the top or bottom, hide the arrow
                setTimeout(() => {
                    if (galleryHeaderList.scrollTop === 0) {
                        [...galleryHeaderListArrows].find((arrowInner) => arrowInner.getAttribute('data-direction') === 'prev').classList.remove('active');
                    } else {
                        [...galleryHeaderListArrows].find((arrowInner) => arrowInner.getAttribute('data-direction') === 'prev').classList.add('active');
                    }
                    if (galleryHeaderList.scrollTop === galleryHeaderList.scrollHeight - galleryHeaderList.clientHeight) {
                        [...galleryHeaderListArrows].find((arrowInner) => arrowInner.getAttribute('data-direction') === 'next').classList.remove('active');
                    } else {
                        [...galleryHeaderListArrows].find((arrowInner) => arrowInner.getAttribute('data-direction') === 'next').classList.add('active');
                    }
                }, 250);
            });
            arrow.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    arrow.click();
                }
            });
            });
    };

    initGalleryImageModal() {
        const galleryModalList = document.querySelector(`.${componentName}__gallery-modal-list-container`);
        if (galleryModalList) {
            const galleryModalListArrows = document.querySelectorAll(`.${componentName}__gallery-modal-list-arrow`);
            if (galleryModalList.scrollWidth > galleryModalList.clientWidth) {
                Array.from(galleryModalListArrows).forEach((arrow) => {
                    arrow.classList.add('active');
                });
            }

            const galleryModalListResizeObserver = new ResizeObserver((entries) => {
                const galleryModalList = entries[0].target;
                const galleryModalListArrows = galleryModalList.parentElement.querySelectorAll(`.${componentName}__gallery-modal-list-arrow`);
                if (galleryModalList.scrollWidth > galleryModalList.clientWidth) {
                    Array.from(galleryModalListArrows).forEach((arrow) => {
                        arrow.classList.add('active');
                    });
                } else {
                    Array.from(galleryModalListArrows).forEach((arrow) => {
                        arrow.classList.remove('active');
                    });
                }
            });
            galleryModalListResizeObserver.observe(galleryModalList);
        }
    }

    /**
     * Initialize Component
     */
    init() {
        // Remove default carousel class on load
        this.element.classList.remove('carousel');

        this.initGalleryImageModal();
        // Fire Init Funcs
        if(!isEditModeFooter() && this.elements.thumbnails.length > 0) {
            this.addEventListeners();
        }

        if (this.elements.container.querySelector('.video')) {
            window.dispatchEvent(new CustomEvent('init-video-players')); // Trigger video initialization
        }
    }
}

export default MultimediaGrid;

registerComponent(`.${componentName}`, MultimediaGrid);
